import { createContext, useContext, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { PageShell, PageHeader } from '@/components/shared/layout'
import ToolsLayout from '@/components/layout/ToolsLayout'
import { cn } from '@/lib/utils'

/** Set by hosts that already draw the app chrome (dev states gallery). */
export const ToolChromeProvided = createContext(false)

/**
 * Paper "Marketing tools · <tool> · 1440": inputs in a gray well on the left,
 * a 320px result card in its own well on the right. Tools render inside
 * DashboardLayout under /workspace|/p, or standalone under /tools.
 */
export function ToolPage({
  title,
  description,
  inputs,
  footer,
  aside,
}: {
  title: string
  description: string
  inputs: ReactNode
  /** Sits under the inputs well, right-aligned (e.g. the generate button). */
  footer?: ReactNode
  aside?: ReactNode
}) {
  const params = useParams()
  const chromeProvided = useContext(ToolChromeProvided)
  const hasContext = chromeProvided || Boolean(params.workspaceId || params.propertySlugId)
  const page = (
    <PageShell>
      <PageHeader title={title} description={description} className="mb-[18px]" />
      <div className="flex flex-col items-start gap-5 lg:flex-row">
        <div className="flex w-full min-w-0 flex-1 flex-col gap-4">
          <div className="well flex flex-col gap-2">{inputs}</div>
          {footer && <div className="flex justify-end">{footer}</div>}
        </div>
        {aside && <div className="well w-full shrink-0 lg:w-auto">{aside}</div>}
      </div>
    </PageShell>
  )
  return hasContext ? page : <ToolsLayout>{page}</ToolsLayout>
}

export function ToolCard({
  title,
  description,
  tag,
  action,
  children,
  className,
}: {
  title: string
  description?: string
  /** Neutral chip after the title ("Required", "Optional"). */
  tag?: string
  action?: ReactNode
  children?: ReactNode
  className?: string
}) {
  return (
    <section className={cn('surface-raised flex flex-col gap-4 p-5', className)}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold leading-[18px] text-foreground">{title}</h2>
            {tag && (
              <span className="flex h-5 items-center rounded-md bg-canvas px-[7px] text-xs font-medium leading-4 text-foreground/60">
                {tag}
              </span>
            )}
          </div>
          {description && <p className="text-xs leading-4 text-foreground/60">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

export function ToolFieldRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-3 sm:flex-row">{children}</div>
}

export function ToolNumberField({
  id,
  label,
  value,
  onChange,
  placeholder,
  max,
  step,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  max?: number
  step?: number
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <label htmlFor={id} className="text-xs font-medium leading-4 text-foreground">
        {label}
      </label>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        min={0}
        max={max}
        step={step}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-lg px-3 font-sans text-[13px] tabular-nums"
      />
    </div>
  )
}

export function ToolTextField({
  id,
  label,
  hint,
  value,
  onChange,
  placeholder,
  error,
  type = 'text',
  list,
}: {
  id: string
  label: string
  hint?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  error?: string
  type?: 'text' | 'url'
  list?: string
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <label htmlFor={id} className="flex gap-1.5 text-xs leading-4">
        <span className="font-medium text-foreground">{label}</span>
        {hint && <span className="text-foreground/60">· {hint}</span>}
      </label>
      <Input
        id={id}
        type={type}
        list={list}
        placeholder={placeholder}
        value={value}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-lg px-3 text-[13px]"
      />
      {error && (
        <p id={`${id}-error`} className="text-xs leading-4 text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}

/** Paper segmented control: canvas track, raised white active segment, ink text. */
export function ToolSegmented<T extends string | number>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex shrink-0 gap-0.5 rounded-lg bg-canvas p-0.5">
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={cn(
              'h-7 rounded-md px-2.5 text-xs font-medium leading-4 transition-colors',
              active
                ? 'bg-card text-foreground shadow-[0_0_0_1px_rgb(9_9_11/0.08),0_1px_2px_rgb(0_0_0/0.04)]'
                : 'text-foreground/60 hover:text-foreground',
            )}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export function ToolResultCard({
  title,
  badge,
  value,
  caption,
  empty,
  answer = true,
  children,
}: {
  title: string
  badge?: ReactNode
  value?: string
  /** Orange marks the answer (DESIGN.md); unsettled results stay ink. */
  answer?: boolean
  caption?: ReactNode
  empty?: string
  children?: ReactNode
}) {
  return (
    <section aria-live="polite" className="surface-raised flex w-full flex-col gap-4 p-5 lg:w-[320px]">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold leading-[18px] text-foreground">{title}</h2>
        {badge}
      </div>
      {value === undefined ? (
        <p className="text-[13px] leading-5 text-foreground/60">{empty}</p>
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <p
              className={cn(
                'text-[32px] font-semibold leading-[38px] tracking-[-0.02em] tabular-nums',
                answer ? 'text-primary-strong' : 'text-foreground',
              )}
            >
              {value}
            </p>
            {caption && <p className="text-[13px] leading-5 text-foreground/60">{caption}</p>}
          </div>
        </>
      )}
      {children}
    </section>
  )
}

export function ToolStatRows({ rows }: { rows: { label: string; value: string; mono?: boolean }[] }) {
  return (
    <dl className="flex flex-col">
      {rows.map((r) => (
        <div key={r.label} className="flex justify-between gap-3 border-t border-black/[0.035] py-[9px] dark:border-white/[0.06]">
          <dt className="text-[13px] leading-[18px] text-foreground/60">{r.label}</dt>
          <dd
            className={cn(
              'text-foreground tabular-nums',
              r.mono ? 'font-mono text-xs leading-[18px]' : 'text-[13px] font-medium leading-[18px]',
            )}
          >
            {r.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export type VerdictTone = 'answer' | 'caution' | 'neutral'

export function ToolVerdict({ tone, children }: { tone: VerdictTone; children: ReactNode }) {
  return (
    <span
      className={cn(
        'flex h-[22px] shrink-0 items-center rounded-md px-2 text-xs font-medium leading-4 ring-1 ring-inset',
        tone === 'answer' && 'bg-primary-tint text-primary-strong ring-primary-tint-ring',
        tone === 'caution' && 'bg-warning-tint text-warning ring-warning/15',
        tone === 'neutral' && 'bg-canvas text-foreground/60 ring-transparent',
      )}
    >
      {children}
    </span>
  )
}

export function ToolGuide({ title, steps }: { title: string; steps: string[] }) {
  return (
    <section className="surface-raised flex w-full flex-col gap-3.5 p-5 lg:w-[320px]">
      <h2 className="text-[13px] font-semibold leading-4 text-foreground">{title}</h2>
      <ol className="flex flex-col gap-3.5">
        {steps.map((step, i) => (
          <li key={step} className="flex items-start gap-2.5">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-canvas text-xs font-semibold leading-4 text-foreground">
              {i + 1}
            </span>
            <span className="text-[13px] leading-5 text-foreground">{step}</span>
          </li>
        ))}
      </ol>
    </section>
  )
}
